<template>
  <HrPageLayout
    title="Leave Planning"
    subtitle="Department leave schedules and terminal leave notifications"
    icon="📅"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Leave & Planning' },
      { label: 'Leave Planning' }
    ]"
  >
    <template #header-actions>
      <button
        v-if="activeTab === 'schedules'"
        type="button"
        class="hr-btn hr-btn-success"
        @click="openScheduleModal"
      >
        <i class="bx bx-plus-circle"></i> Add schedule
      </button>
    </template>

    <div class="lp-page">
      <div class="lp-tabs" role="tablist">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="lp-tab"
          :class="{ active: activeTab === t.id }"
          role="tab"
          :aria-selected="activeTab === t.id"
          @click="switchTab(t.id)"
        >
          <i :class="t.icon"></i>
          <span>{{ t.label }}</span>
        </button>
      </div>

      <!-- Schedules -->
      <div v-show="activeTab === 'schedules'" class="lp-panel">
        <div class="lp-toolbar">
          <div class="lp-field">
            <label>Year</label>
            <select v-model="scheduleYear" class="lp-input" @change="loadSchedules">
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="lp-field grow">
            <label>Department</label>
            <select v-model="filterDeptId" class="lp-input" @change="loadSchedules">
              <option value="">All departments</option>
              <option
                v-for="d in departments"
                :key="d.id || d.departmentID"
                :value="d.departmentID || d.id"
              >
                {{ d.departmentName }}
              </option>
            </select>
          </div>
          <div class="lp-field actions">
            <label>&nbsp;</label>
            <div class="lp-btn-row">
              <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="loadSchedules">
                <i class="bx bx-refresh"></i> Refresh
              </button>
              <button type="button" class="hr-btn hr-btn-primary" @click="openScheduleModal">
                <i class="bx bx-plus"></i> Add schedule
              </button>
            </div>
          </div>
        </div>

        <div v-if="loading" class="lp-empty">
          <div class="hr-spinner"></div>
          <p>Loading schedules…</p>
        </div>
        <div v-else-if="!schedules.length" class="lp-empty">
          <i class="bx bx-calendar-plus lp-empty-icon"></i>
          <h4>No schedules for {{ scheduleYear }}</h4>
          <p>Plan department leave windows so teams can coordinate coverage.</p>
          <button type="button" class="hr-btn hr-btn-primary" @click="openScheduleModal">
            <i class="bx bx-plus"></i> Add schedule
          </button>
        </div>
        <div v-else class="lp-table-wrap">
          <table class="lp-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Leave type</th>
                <th>Year</th>
                <th>Planned dates</th>
                <th>Notes</th>
                <th class="center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in schedules" :key="s.id">
                <td>
                  <strong>{{ s.department_name }}</strong>
                </td>
                <td>
                  <span class="lp-chip">{{ s.leave_type_name || 'General' }}</span>
                </td>
                <td>{{ s.schedule_year }}</td>
                <td class="dates">{{ s.planned_dates || '—' }}</td>
                <td class="notes">{{ s.notes || '—' }}</td>
                <td class="center">
                  <button type="button" class="hr-btn hr-btn-sm hr-btn-info" @click="editSchedule(s)">
                    <i class="bx bx-edit"></i> Edit
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Terminal -->
      <div v-show="activeTab === 'terminal'" class="lp-panel">
        <div class="lp-toolbar">
          <div class="lp-field grow">
            <p class="lp-hint">Employees approaching or on terminal leave (retirement-linked).</p>
          </div>
          <div class="lp-field actions">
            <label>&nbsp;</label>
            <button type="button" class="hr-btn hr-btn-secondary" :disabled="loading" @click="loadTerminal">
              <i class="bx bx-refresh"></i> Refresh
            </button>
          </div>
        </div>
        <div v-if="loading" class="lp-empty">
          <div class="hr-spinner"></div>
          <p>Loading notifications…</p>
        </div>
        <div v-else-if="!terminalNotifications.length" class="lp-empty">
          <i class="bx bx-bell-off lp-empty-icon"></i>
          <h4>No terminal leave notifications</h4>
          <p>Nothing queued for terminal leave right now.</p>
        </div>
        <div v-else class="lp-table-wrap">
          <table class="lp-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Retirement date</th>
                <th>Terminal leave start</th>
                <th>Terminal leave end</th>
                <th class="center">Days</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in terminalNotifications" :key="n.id">
                <td><strong>{{ n.firstName }} {{ n.lastName }}</strong></td>
                <td>{{ n.empDept || n.department_id || '—' }}</td>
                <td>{{ n.retirement_date || '—' }}</td>
                <td>{{ n.terminal_leave_start || '—' }}</td>
                <td>{{ n.terminal_leave_end || '—' }}</td>
                <td class="center">{{ n.days_granted || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Add / Edit schedule dialog -->
      <div
        v-if="showScheduleModal"
        class="lp-overlay"
        role="dialog"
        aria-modal="true"
        @keydown.esc="closeModal"
        @click.self="closeModal"
      >
        <div class="lp-modal">
          <header class="lp-modal-head">
            <div>
              <p class="eyebrow">{{ editingSchedule ? 'Update' : 'New' }} department schedule</p>
              <h3>{{ editingSchedule ? 'Edit schedule' : 'Add schedule' }}</h3>
            </div>
            <button type="button" class="lp-icon-btn" aria-label="Close" @click="closeModal">
              <i class="bx bx-x"></i>
            </button>
          </header>

          <div class="lp-modal-body">
            <div class="lp-form-grid">
              <div class="lp-field span-full">
                <label>Department <span class="req">*</span></label>
                <select v-model="scheduleForm.department_id" class="lp-input" required>
                  <option value="">Select department</option>
                  <option
                    v-for="d in departments"
                    :key="d.id || d.departmentID"
                    :value="d.departmentID || d.id"
                  >
                    {{ d.departmentName }}
                  </option>
                </select>
              </div>

              <div class="lp-field">
                <label>Schedule year</label>
                <select v-model="scheduleForm.schedule_year" class="lp-input">
                  <option v-for="y in yearOptions" :key="'sy-' + y" :value="y">{{ y }}</option>
                </select>
              </div>

              <div class="lp-field">
                <label>Leave type</label>
                <input
                  v-model="scheduleForm.leave_type_name"
                  type="text"
                  class="lp-input"
                  list="lp-leave-types"
                  placeholder="e.g. Annual leave"
                />
                <datalist id="lp-leave-types">
                  <option v-for="lt in leaveTypeSuggestions" :key="lt" :value="lt" />
                </datalist>
              </div>

              <div class="lp-field span-full">
                <label>Planned period</label>
                <div class="lp-range">
                  <input v-model="scheduleForm.date_from" type="date" class="lp-input" @change="syncPlannedDates" />
                  <span class="lp-range-sep">to</span>
                  <input v-model="scheduleForm.date_to" type="date" class="lp-input" @change="syncPlannedDates" />
                </div>
                <p class="lp-hint sm">Optional: pick a range, or describe free-form below.</p>
              </div>

              <div class="lp-field span-full">
                <label>Planned dates (text)</label>
                <input
                  v-model="scheduleForm.planned_dates"
                  type="text"
                  class="lp-input"
                  placeholder="e.g. 2026-01-01 to 2026-01-15, or Q1 windows"
                />
              </div>

              <div class="lp-field span-full">
                <label>Notes</label>
                <textarea
                  v-model="scheduleForm.notes"
                  class="lp-input ta"
                  rows="3"
                  placeholder="Coverage notes, blackout periods, handover…"
                ></textarea>
              </div>
            </div>
            <p v-if="formError" class="lp-error">{{ formError }}</p>
          </div>

          <footer class="lp-modal-foot">
            <button type="button" class="hr-btn hr-btn-secondary" :disabled="saving" @click="closeModal">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveSchedule">
              <i class="bx bx-save"></i>
              {{ saving ? 'Saving…' : (editingSchedule ? 'Update schedule' : 'Save schedule') }}
            </button>
          </footer>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

export default {
  name: "LeavePlanning",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: "schedules",
      tabs: [
        { id: "schedules", label: "Department leave schedules", icon: "bx bx-calendar" },
        { id: "terminal", label: "Terminal leave notifications", icon: "bx bx-bell" }
      ],
      scheduleYear: new Date().getFullYear(),
      yearOptions: [],
      filterDeptId: "",
      departments: [],
      schedules: [],
      terminalNotifications: [],
      showScheduleModal: false,
      editingSchedule: null,
      saving: false,
      loading: false,
      formError: "",
      leaveTypeSuggestions: ["Annual leave", "Maternity", "Paternity", "Study leave", "Sick leave", "Compassionate"],
      scheduleForm: {
        id: null,
        department_id: "",
        department_name: "",
        schedule_year: new Date().getFullYear(),
        leave_type_name: "",
        planned_dates: "",
        date_from: "",
        date_to: "",
        notes: ""
      }
    };
  },
  created() {
    const y = new Date().getFullYear();
    for (let i = y - 1; i <= y + 3; i++) this.yearOptions.push(i);
    this.getDepartments();
    this.load();
  },
  methods: {
    switchTab(id) {
      this.activeTab = id;
      this.load();
    },
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl)
        .then((res) => {
          if (res.data && res.data.success && res.data.data && res.data.data.departments) {
            this.departments = res.data.data.departments;
          }
        })
        .catch(() => {});
    },
    load() {
      if (this.activeTab === "schedules") this.loadSchedules();
      else this.loadTerminal();
    },
    loadSchedules() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getDepartmentLeaveSchedules");
      fd.append("year", this.scheduleYear);
      if (this.filterDeptId) fd.append("departmentId", this.filterDeptId);
      execute(fd, authUrl)
        .then((res) => {
          this.schedules =
            res.data && res.data.success && res.data.data && res.data.data.schedules
              ? res.data.data.schedules
              : [];
        })
        .catch(() => {
          this.schedules = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadTerminal() {
      this.loading = true;
      const fd = new FormData();
      fd.append("function", "getTerminalLeaveNotifications");
      execute(fd, authUrl)
        .then((res) => {
          this.terminalNotifications =
            res.data && res.data.success && res.data.data && res.data.data.notifications
              ? res.data.data.notifications
              : [];
        })
        .catch(() => {
          this.terminalNotifications = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openScheduleModal() {
      this.editingSchedule = null;
      this.formError = "";
      this.scheduleForm = {
        id: null,
        department_id: this.filterDeptId || "",
        department_name: "",
        schedule_year: this.scheduleYear,
        leave_type_name: "",
        planned_dates: "",
        date_from: "",
        date_to: "",
        notes: ""
      };
      this.showScheduleModal = true;
    },
    editSchedule(s) {
      this.editingSchedule = s;
      this.formError = "";
      let dateFrom = "";
      let dateTo = "";
      const m = String(s.planned_dates || "").match(
        /(\d{4}-\d{2}-\d{2})\s*(?:to|-)\s*(\d{4}-\d{2}-\d{2})/i
      );
      if (m) {
        dateFrom = m[1];
        dateTo = m[2];
      }
      this.scheduleForm = {
        id: s.id,
        department_id: s.department_id,
        department_name: s.department_name,
        schedule_year: s.schedule_year,
        leave_type_name: s.leave_type_name || "",
        planned_dates: s.planned_dates || "",
        date_from: dateFrom,
        date_to: dateTo,
        notes: s.notes || ""
      };
      this.showScheduleModal = true;
    },
    closeModal() {
      if (this.saving) return;
      this.showScheduleModal = false;
      this.formError = "";
    },
    syncPlannedDates() {
      const a = this.scheduleForm.date_from;
      const b = this.scheduleForm.date_to;
      if (a && b) {
        this.scheduleForm.planned_dates = `${a} to ${b}`;
      } else if (a && !b) {
        this.scheduleForm.planned_dates = a;
      }
    },
    saveSchedule() {
      this.formError = "";
      if (!this.scheduleForm.department_id) {
        this.formError = "Please select a department.";
        return;
      }
      this.syncPlannedDates();
      const d = this.departments.find(
        (x) => String(x.departmentID || x.id) === String(this.scheduleForm.department_id)
      );
      this.scheduleForm.department_name = d ? d.departmentName : "";
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "saveDepartmentLeaveSchedule");
      ["id", "department_id", "department_name", "schedule_year", "leave_type_name", "planned_dates", "notes"].forEach(
        (k) => {
          const v = this.scheduleForm[k];
          fd.append(k, v == null ? "" : v);
        }
      );
      execute(fd, authUrl)
        .then((res) => {
          this.saving = false;
          if (res.data && res.data.success) {
            this.showScheduleModal = false;
            toast.success(this.editingSchedule ? "Schedule updated" : "Schedule saved");
            this.loadSchedules();
          } else {
            this.formError = (res.data && res.data.message) || "Save failed";
            toast.error(this.formError);
          }
        })
        .catch((e) => {
          this.saving = false;
          this.formError = e?.response?.data?.message || e?.message || "Save failed";
          toast.error(this.formError);
        });
    }
  }
};
</script>

<style scoped>
.lp-page {
  padding-bottom: 28px;
}
.lp-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.lp-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #d0d7e2;
  border-radius: 10px;
  background: #fff;
  color: #334155;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}
.lp-tab i {
  font-size: 18px;
}
.lp-tab:hover {
  border-color: #94a3b8;
}
.lp-tab.active {
  background: #0c2d48;
  border-color: #0c2d48;
  color: #fff;
}
.lp-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 18px 20px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.lp-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
  margin-bottom: 16px;
}
.lp-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
}
.lp-field.grow {
  flex: 1 1 220px;
}
.lp-field label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.02em;
}
.lp-field.actions {
  min-width: auto;
}
.lp-btn-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.lp-input {
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 9px 11px;
  font-size: 13px;
  color: #0f172a;
  background: #fff;
  min-height: 40px;
}
.lp-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.lp-input.ta {
  min-height: 88px;
  resize: vertical;
  font-family: inherit;
}
.lp-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.lp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.lp-table th {
  text-align: left;
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  padding: 11px 12px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}
.lp-table td {
  padding: 12px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #0f172a;
}
.lp-table tr:last-child td {
  border-bottom: none;
}
.lp-table .center {
  text-align: center;
}
.lp-table .dates {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.lp-table .notes {
  max-width: 280px;
  color: #64748b;
}
.lp-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
}
.lp-empty {
  text-align: center;
  padding: 48px 16px;
  color: #64748b;
}
.lp-empty-icon {
  font-size: 42px;
  color: #94a3b8;
  display: block;
  margin-bottom: 8px;
}
.lp-empty h4 {
  margin: 8px 0 4px;
  color: #0f172a;
  font-size: 16px;
}
.lp-empty p {
  margin: 0 0 16px;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}
.lp-hint {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}
.lp-hint.sm {
  font-size: 12px;
  margin-top: 4px;
}
.lp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.lp-modal {
  background: #fff;
  width: min(560px, 100%);
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 760px);
}
.lp-modal-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc, #fff);
}
.lp-modal-head .eyebrow {
  margin: 0 0 2px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}
.lp-modal-head h3 {
  margin: 0;
  font-size: 18px;
  color: #0c2d48;
}
.lp-icon-btn {
  border: none;
  background: #f1f5f9;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 22px;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.lp-modal-body {
  padding: 16px 20px 8px;
  overflow-y: auto;
}
.lp-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 14px;
}
.lp-form-grid .span-full {
  grid-column: 1 / -1;
}
.req {
  color: #dc2626;
}
.lp-range {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
}
.lp-range-sep {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}
.lp-error {
  margin: 10px 0 0;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 600;
}
.lp-modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
}
@media (max-width: 640px) {
  .lp-form-grid {
    grid-template-columns: 1fr;
  }
  .lp-range {
    grid-template-columns: 1fr;
  }
  .lp-range-sep {
    display: none;
  }
}
</style>
